import { useState, useEffect } from 'react';
import { useScrollFadeIn } from '../../../hooks/useScrollFadeIn';

const colorVariants = [
  {
    id: 'black',
    label: 'ブラック',
    labelEn: 'Black',
    swatch: '#1a1a1a',
    border: '#444',
    tag: '定番人気',
    tagColor: 'bg-gray-800 text-white',
    url: 'https://mever.design/products/mever-chair-pro-ver2?variant=51384900911394',
    stock: '残りわずか',
    stockColor: 'text-red-400',
  },
  {
    id: 'gray',
    label: 'グレー',
    labelEn: 'Gray',
    swatch: '#888880',
    border: '#aaa',
    tag: '洗練イメージ',
    tagColor: 'bg-[#888880] text-white',
    url: 'https://mever.design/products/mever-chair-pro-ver2?variant=51384900878626',
    stock: '残りわずか',
    stockColor: 'text-red-400',
  },
];

const mobileFeatures = [
  {
    highlight: '3D',
    label: 'ヘッドレスト',
    desc: '3軸可動で首・頭を完全サポート',
    icon: 'ri-focus-3-line',
  },
  {
    highlight: '6D',
    label: 'アームレスト',
    desc: '6軸で自在に調整可能',
    icon: 'ri-settings-3-line',
  },
  {
    highlight: '16',
    label: 'ヶ所の調整',
    desc: '全身にフィットする精密設計',
    icon: 'ri-equalizer-line',
  },
];

function MobileFeatureCard({ item, delay }: { item: typeof mobileFeatures[0]; delay: number }) {
  const { ref, isVisible } = useScrollFadeIn({ delay, threshold: 0.15 });

  return (
    <div
      ref={ref}
      className="relative flex items-center overflow-hidden rounded-2xl transition-all duration-700 ease-out"
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.03) 100%)',
        border: '1px solid rgba(255,255,255,0.10)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
      }}
    >
      <div
        className="flex-shrink-0 flex flex-col items-center justify-center px-6 py-5"
        style={{ minWidth: '88px' }}
      >
        <span
          className="font-black leading-none tracking-tighter"
          style={{
            fontSize: '3.2rem',
            background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.45) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.04em',
          }}
        >
          {item.highlight}
        </span>
      </div>

      <div
        className="flex-shrink-0 self-stretch"
        style={{
          width: '1px',
          background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.15) 30%, rgba(255,255,255,0.15) 70%, transparent 100%)',
        }}
      />

      <div className="flex-1 flex items-center gap-4 px-5 py-5">
        <div
          className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <i className={`${item.icon} text-lg`} style={{ color: 'rgba(255,255,255,0.75)' }}></i>
        </div>
        <div>
          <p
            className="font-black leading-tight mb-1"
            style={{ fontSize: '13px', color: 'rgba(255,255,255,0.92)' }}
          >
            {item.label}
          </p>
          <p
            className="leading-snug"
            style={{ fontSize: '11px', color: 'rgba(255,255,255,0.38)' }}
          >
            {item.desc}
          </p>
        </div>
      </div>

      <div
        className="absolute right-0 top-0 bottom-0 w-0.5 rounded-r-2xl"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)',
        }}
      />
    </div>
  );
}

export default function Hero() {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [selectedColor, setSelectedColor] = useState<string>('black');
  const { ref, isVisible } = useScrollFadeIn({ delay: 0 });

  useEffect(() => {
    const targetDate = new Date('2026-03-31T23:59:59');
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();
      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      }
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const selectedVariant = colorVariants.find(v => v.id === selectedColor) ?? colorVariants[0];

  return (
    <section
      ref={ref}
      className={`relative overflow-hidden transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      {/* ティッカーバナー */}
      <div
        className="relative w-full overflow-hidden z-50"
        style={{
          background: '#080808',
          borderBottom: '1px solid rgba(168,85,247,0.15)',
        }}
      >
        <div className="flex overflow-hidden py-2">
          <div
            className="flex items-center gap-0 whitespace-nowrap"
            style={{ animation: 'ticker-scroll 28s linear infinite', willChange: 'transform' }}
          >
            {[...Array(2)].map((_, setIdx) => (
              <div key={setIdx} className="flex items-center gap-0">
                {[
                  { text: '各色50脚限定' },
                  { text: '予約限定 30% OFF' },
                  { text: '5年保証付き' },
                  { text: '送料無料' },
                  { text: '2026.3.6 – 3.31 キャンペーン期間' },
                  { text: '30日返品保証' },
                ].map((item, idx) => (
                  <div key={`${setIdx}-${idx}`} className="flex items-center">
                    <span className="inline-flex items-center px-6 text-[11px] font-medium tracking-[0.15em] uppercase text-white/50">
                      {item.text}
                    </span>
                    <span className="text-purple-900/40 text-sm font-thin select-none">✦</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes gold-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .gold-shimmer::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%);
          animation: gold-shimmer 2.5s ease-in-out infinite;
        }
      `}</style>

      <div className="relative overflow-hidden bg-[#080808]">

        {/* ── デスクトップ ── */}
        <div className="hidden md:block">

          <div className="relative w-full min-h-screen flex flex-col items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-[#080808]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-purple-500/5 blur-[120px] pointer-events-none" />

            <div className="relative z-10 w-full px-10 flex flex-col items-center text-center">
              <div className="mb-16">
                <img
                  src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/a6432ee4b37c265c1d5171353c491488.png"
                  alt="MeVER Logo"
                  className="h-6 w-auto object-contain brightness-0 invert opacity-60"
                />
              </div>

              <div className="w-full max-w-[1000px] mx-auto mb-20">
                <div className="overflow-hidden mb-8">
                  <p className="text-[clamp(2.8rem,5.5vw,5.5rem)] font-black text-white leading-[1.15] tracking-[-0.03em]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    どんな姿勢にも、アームとヘッドレストがついてくる。
                  </p>
                </div>
                <div className="overflow-hidden mb-6">
                  <p className="text-[clamp(1rem,1.5vw,1.25rem)] font-light text-white/55 leading-[1.9] tracking-[0.04em]">
                    東京の空間設計チームが辿り着いた、妥協なきワークスタイル。Pro v2 新登場/先行予約限定30%OFF。
                  </p>
                </div>

                {/* デスクトップ動画 */}
                <div className="relative w-full mb-10">
                  <div
                    className="absolute -inset-4 rounded-3xl pointer-events-none"
                    style={{
                      background: 'radial-gradient(ellipse at center, rgba(168,85,247,0.15) 0%, transparent 70%)',
                      filter: 'blur(20px)',
                    }}
                  />
                  <div
                    className="absolute -inset-px rounded-2xl pointer-events-none"
                    style={{
                      background: 'linear-gradient(135deg, rgba(168,85,247,0.25) 0%, rgba(255,255,255,0.05) 50%, rgba(168,85,247,0.15) 100%)',
                    }}
                  />
                  <div
                    className="relative w-full aspect-video rounded-2xl overflow-hidden"
                    style={{
                      boxShadow: '0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
                    }}
                  >
                    <iframe
                      src="https://www.youtube.com/embed/G8i6MBD5O8s?autoplay=1&loop=1&playlist=G8i6MBD5O8s&mute=1&controls=1&modestbranding=1&rel=0&playsinline=1"
                      title="MeVER Chair Pro v2.0 製品動画"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                      style={{ border: 'none' }}
                    />
                  </div>
                </div>

                {/* PCバッジ行 */}
                <div className="flex items-center gap-2 flex-wrap mb-6">
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-red-500/10 border border-red-400/30 text-red-300 text-[11px] font-semibold tracking-[0.1em] uppercase rounded-full whitespace-nowrap">
                    <span className="text-base leading-none">🇯🇵</span>
                    日本ブランド
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-amber-400/10 border border-amber-400/30 text-amber-300 text-[11px] font-semibold tracking-[0.1em] uppercase rounded-full whitespace-nowrap">
                    <span className="w-2 h-2 rounded-full bg-amber-400 inline-block animate-pulse"></span>
                    新生活応援キャンペーン
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-amber-400/10 border border-amber-400/30 text-amber-300 text-[11px] font-semibold tracking-[0.1em] rounded-full whitespace-nowrap">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-check-line text-xs"></i></span>5年保証
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-amber-400/10 border border-amber-400/30 text-amber-300 text-[11px] font-semibold tracking-[0.1em] rounded-full whitespace-nowrap">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-go-back-line text-xs"></i></span>30日返品保証
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <img
                    src="https://static.readdy.ai/image/a70e5a8c420a179c893b7b588530b5c7/34aa351f4c9ed77898376e331745f99c.svg"
                    alt="Paidy"
                    className="h-5 w-auto object-contain flex-shrink-0"
                    style={{ filter: 'brightness(0) invert(1)', opacity: 0.85 }}
                  />
                  <span className="text-sm text-white/50">なら</span>
                  <span className="text-base font-black text-white/90">月々7,470円×12回から</span>
                  <span className="ml-auto text-[10px] text-white/35 whitespace-nowrap">手数料無料</span>
                </div>
              </div>

              <div className="relative bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 opacity-30">
                <span className="text-[9px] tracking-[0.4em] uppercase text-white/40 font-light">Scroll</span>
                <div className="w-px h-12 bg-gradient-to-b from-white/20 to-transparent" />
              </div>
            </div>
          </div>

          {/* SECTION 2: 購入エリア（デスクトップ） */}
          <div className="relative bg-white w-full">
            <div className="relative z-10 max-w-3xl mx-auto px-10 py-32 flex flex-col items-center gap-14">

              <div className="w-full text-center space-y-4">
                <p className="text-[10px] tracking-[0.4em] uppercase text-gray-400 font-medium">Product Details</p>
                <h2 className="text-3xl font-bold text-gray-900 leading-[1.3] tracking-tight">
                  v1から進化した3Dヘッドレスト・6Dアームレスト
                </h2>
                <p className="text-base text-gray-500 leading-[1.8]">
                  16カ所の調整機構で全身をサポート。
                </p>
              </div>

              <div className="w-full grid grid-cols-3 gap-6">
                {[
                  { icon: 'ri-focus-3-line', highlight: '3D', label: 'ヘッドレスト', desc: '3軸可動で首・頭を完全サポート' },
                  { icon: 'ri-settings-3-line', highlight: '6D', label: 'アームレスト', desc: '6軸で自在に調整可能' },
                  { icon: 'ri-equalizer-line', highlight: '16', label: 'ヶ所の調整', desc: '全身にフィットする精密設計' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex flex-col items-center gap-5 px-6 py-10 rounded-3xl cursor-pointer"
                    style={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f7f7f7 100%)',
                      border: '1.5px solid rgba(0,0,0,0.08)',
                      boxShadow: '0 4px 32px rgba(0,0,0,0.06)',
                      transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease, border-color 0.4s ease',
                    }}
                    onMouseEnter={e => {
                      const el = e.currentTarget as HTMLDivElement;
                      el.style.transform = 'translateY(-8px) scale(1.04)';
                      el.style.boxShadow = '0 24px 56px rgba(0,0,0,0.13)';
                      el.style.borderColor = 'rgba(0,0,0,0.18)';
                    }}
                    onMouseLeave={e => {
                      const el = e.currentTarget as HTMLDivElement;
                      el.style.transform = 'translateY(0) scale(1)';
                      el.style.boxShadow = '0 4px 32px rgba(0,0,0,0.06)';
                      el.style.borderColor = 'rgba(0,0,0,0.08)';
                    }}
                  >
                    <div className="w-16 h-16 flex items-center justify-center rounded-2xl" style={{ background: 'rgba(0,0,0,0.05)' }}>
                      <i className={`${item.icon} text-4xl`} style={{ color: '#1a1a1a' }}></i>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <span
                        className="text-[4.5rem] font-black leading-none tracking-tight"
                        style={{
                          background: 'linear-gradient(135deg, #1a1a1a, #555555)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        {item.highlight}
                      </span>
                      <span className="text-[1.05rem] font-bold text-gray-800 tracking-tight">{item.label}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="w-full border border-gray-100 rounded-3xl p-8 bg-white">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-xs text-gray-400 line-through tracking-wide mb-1">¥128,700（税込）</p>
                    <div className="flex items-baseline gap-2">
                      <p className="text-[3.5rem] font-black text-gray-900 tracking-tight leading-none">¥89,650</p>
                      <p className="text-sm text-gray-400">税込</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center w-20 h-20 rounded-full bg-gray-900 flex-shrink-0">
                    <p className="text-[9px] text-gray-400 font-bold tracking-widest leading-none mb-0.5">予約限定</p>
                    <p className="text-3xl font-black text-white leading-none">30<span className="text-base">%</span></p>
                    <p className="text-[9px] text-gray-400 font-bold tracking-widest leading-none mt-0.5">OFF</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-xl mb-6">
                  <img
                    src="https://static.readdy.ai/image/a70e5a8c420a179c893b7b588530b5c7/34aa351f4c9ed77898376e331745f99c.svg"
                    alt="Paidy"
                    className="h-5 w-auto object-contain flex-shrink-0"
                  />
                  <span className="text-sm text-gray-500">なら</span>
                  <span className="text-base font-black text-gray-900">月々7,470円</span>
                  <span className="text-sm text-gray-500">から。分割手数料無料</span>
                </div>

                <div className="flex flex-wrap gap-2 pt-5 border-t border-gray-100">
                  {[
                    { icon: 'ri-truck-line', text: '送料無料', link: '' },
                    { icon: 'ri-shield-check-line', text: '5年保証', link: 'https://mever.design/pages/warranty' },
                    { icon: 'ri-calendar-check-line', text: '30日返品保証', link: 'https://mever.design/pages/30%E6%97%A5%E9%96%93%E8%BF%94%E9%87%91%E4%BF%9D%E8%A8%BC%E3%83%9D%E3%83%AA%E3%82%B7%E3%83%BC' },
                    { icon: 'ri-bank-card-line', text: '分割払い可', link: '' },
                    { icon: 'ri-tools-line', text: '組立代行可', link: 'https://mever.design/pages/assembly' },
                  ].map((item) => {
                    const cls = "inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-600 text-[12px] font-semibold rounded-full whitespace-nowrap cursor-pointer hover:bg-gray-200 transition-colors";
                    const inner = (
                      <>
                        <span className="w-4 h-4 flex items-center justify-center"><i className={`${item.icon} text-sm`}></i></span>
                        {item.text}
                      </>
                    );
                    return item.link ? (
                      <a key={item.text} href={item.link} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
                    ) : (
                      <span key={item.text} className={cls}>{inner}</span>
                    );
                  })}
                </div>
              </div>

              <div className="w-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-px flex-1 bg-gray-200" />
                  <p className="text-[12px] font-bold uppercase tracking-[0.3em] leading-none text-gray-500">
                    キャンペーン終了まで
                  </p>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                <div className="flex items-end gap-4 justify-center">
                  {[
                    { value: String(countdown.days).padStart(2, '0'), label: '日' },
                    { value: String(countdown.hours).padStart(2, '0'), label: '時間' },
                    { value: String(countdown.minutes).padStart(2, '0'), label: '分' },
                    { value: String(countdown.seconds).padStart(2, '0'), label: '秒' },
                  ].map((item, idx) => (
                    <div key={item.label} className="flex items-end gap-1.5">
                      {idx > 0 && <span className="text-3xl font-black text-gray-300 leading-none mb-3">:</span>}
                      <div className="flex flex-col items-center">
                        <span className="text-[4rem] font-black text-gray-900 leading-none tabular-nums tracking-tight">{item.value}</span>
                        <span className="text-[11px] text-gray-400 font-semibold tracking-widest mt-1">{item.label}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 relative flex items-center gap-3 px-6 py-4 rounded-2xl overflow-hidden bg-red-50 border border-red-200">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-70"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                  </span>
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-fire-fill text-base text-red-500"></i>
                  </span>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-base font-black tracking-tight text-red-600">各色50脚限定</span>
                    <span className="text-sm text-gray-500 font-medium">ブラック・グレー 在庫わずか</span>
                  </div>
                  <div className="ml-auto flex-shrink-0">
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-black text-red-600 border border-red-300 bg-white">
                      残りわずか
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ── モバイル ── */}
        <div className="md:hidden relative bg-[#0a0a0a]">
          <div className="relative z-10 w-full px-5 pt-10 pb-36 flex flex-col">

            {/* メインビジュアル画像 */}
            <div
              className="relative mb-6"
              style={{ width: '100vw', marginLeft: '-1.25rem' }}
            >
              <img
                src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/8efa144eb603d174ac5ceaf22a2161b6.jpeg"
                alt="どんな姿勢にもアームレストがついてくる。MeVER Chair Pro v2.0"
                className="w-full object-cover object-top"
              />
            </div>

            {/* ゴールドカード */}
            <div className="mb-4">
              <div
                className="relative rounded-xl overflow-hidden px-5 py-4"
                style={{
                  background: 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(10,10,10,0.95) 100%)',
                  border: '1px solid rgba(251,191,36,0.30)',
                  boxShadow: '0 0 40px rgba(245,158,11,0.08), inset 0 1px 0 rgba(255,255,255,0.06)',
                }}
              >
                <div className="relative z-10 flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-1 h-10 rounded-full"
                    style={{ background: 'linear-gradient(180deg, #f59e0b, #d97706)' }}
                  />
                  <p
                    className="text-[1.05rem] font-black leading-[1.35] tracking-tight"
                    style={{
                      fontFamily: "'Noto Serif JP', serif",
                      background: 'linear-gradient(90deg, #fde68a, #fbbf24, #f59e0b)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    東京の空間設計チームが辿り着いた、妥協なきワークスタイル。
                  </p>
                </div>
              </div>
            </div>

            {/* ① ゴールドカード直下の画像（画面幅いっぱい） */}
            <div
              className="relative mb-4"
              style={{ width: '100vw', marginLeft: '-1.25rem' }}
            >
              <img
                src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/2f9944b7516bb2ac5a70d49792603499.jpeg"
                alt="MeVER Chair Pro v2.0 製品イメージ"
                className="w-full object-cover object-top"
              />
            </div>

            {/* ② YouTube動画（正方形・画面幅いっぱい） */}
            <div
              className="relative mb-4"
              style={{ width: '100vw', marginLeft: '-1.25rem' }}
            >
              <div
                className="relative w-full overflow-hidden"
                style={{ aspectRatio: '1 / 1' }}
              >
                <iframe
                  src="https://www.youtube.com/embed/G8i6MBD5O8s?autoplay=1&loop=1&playlist=G8i6MBD5O8s&mute=1&controls=1&modestbranding=1&rel=0&playsinline=1"
                  title="MeVER Chair Pro v2.0 製品動画"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  style={{ border: 'none' }}
                />
              </div>
            </div>

            {/* ③ プロベーシスト画像（YouTube直下） */}
            <div
              className="relative mb-6"
              style={{ width: '100vw', marginLeft: '-1.25rem' }}
            >
              <img
                src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/148328ffeb2b0af5fa2cbf298cb9b026.jpeg"
                alt="プロベーシストが認めたレコーディング演奏専用機 MeVER Chair Pro v2.0"
                className="w-full object-cover object-top"
              />
            </div>

            {/* ④ 米軍基地画像（プロベーシスト画像直下） */}
            <div
              className="relative mb-6"
              style={{ width: '100vw', marginLeft: '-1.25rem' }}
            >
              <img
                src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/ceed49043dcea465c5d27a548a9923f0.jpeg"
                alt="米軍基地が認めた、揺るぎない品質。MeVER Chair Pro 在日米軍基地に正式採用。"
                className="w-full object-cover object-top"
              />
            </div>

            {/* ⑤ コワーキングスペース by favy 画像（米軍基地画像直下） */}
            <div
              className="relative mb-6"
              style={{ width: '100vw', marginLeft: '-1.25rem' }}
            >
              <img
                src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/5200acf3ec9fe565981bba779a60e570.jpeg"
                alt="コワーキングスペース by favy で Pro v1 を体験！場所：アドホック新宿ビル4F"
                className="w-full object-cover object-top"
              />
            </div>

            {/* テキスト＋Paidy */}
            <div className="mb-8">
              <p className="text-[11px] text-white/50 leading-[1.9] mb-4">
                <b className="text-white/70">予約限定30%OFF</b>、ブラック・グレー 各色<b className="text-white/70">50脚限定</b>。
              </p>
              <div
                className="flex items-center gap-2 px-4 py-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}
              >
                <img
                  src="https://static.readdy.ai/image/a70e5a8c420a179c893b7b588530b5c7/34aa351f4c9ed77898376e331745f99c.svg"
                  alt="Paidy"
                  className="h-5 w-auto object-contain flex-shrink-0"
                  style={{ filter: 'brightness(0) invert(1)', opacity: 0.85 }}
                />
                <span className="text-sm text-gray-500">なら</span>
                <span className="text-[13px] font-black text-white/85">月々7,470円×12回から</span>
                <span className="ml-auto text-[10px] text-white/35 whitespace-nowrap">手数料無料</span>
              </div>
            </div>

            {/* 特徴カード */}
            <div className="mb-8 flex flex-col gap-3">
              {mobileFeatures.map((item, idx) => (
                <MobileFeatureCard key={item.label} item={item} delay={idx * 100} />
              ))}
            </div>

            {/* 価格ブロック */}
            <div className="rounded-2xl p-5 mb-6 bg-white border border-gray-100 shadow-sm">

              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[11px] text-gray-400 line-through mb-1">定価 ¥128,700（税込）</p>
                  <div className="flex items-baseline gap-1.5">
                    <p className="text-[2.6rem] font-black text-gray-900 tracking-tight leading-none">¥89,650</p>
                    <p className="text-xs text-gray-400 mb-1">税込</p>
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center w-16 h-16 rounded-full bg-gray-900 flex-shrink-0">
                  <p className="text-[9px] text-gray-400 font-bold tracking-widest leading-none mb-0.5">予約限定</p>
                  <p className="text-[1.4rem] font-black text-white leading-none">30<span className="text-xs">%</span></p>
                  <p className="text-[9px] text-gray-400 font-bold tracking-widest leading-none mt-0.5">OFF</p>
                </div>
              </div>

              <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-xl mb-6">
                <img
                  src="https://static.readdy.ai/image/a70e5a8c420a179c893b7b588530b5c7/34aa351f4c9ed77898376e331745f99c.svg"
                  alt="Paidy"
                  className="h-5 w-auto object-contain flex-shrink-0"
                />
                <span className="text-sm text-gray-500">なら</span>
                <span className="text-base font-black text-gray-900">月々7,470円</span>
                <span className="text-sm text-gray-500">から。分割手数料無料</span>
              </div>

              <div className="flex flex-wrap gap-2 pt-5 border-t border-gray-100">
                {[
                  { icon: 'ri-truck-line', text: '送料無料' },
                  { icon: 'ri-shield-check-line', text: '5年保証' },
                  { icon: 'ri-calendar-check-line', text: '30日返品保証' },
                  { icon: 'ri-bank-card-line', text: '分割払い可' },
                  { icon: 'ri-tools-line', text: '組立代行可' },
                ].map((item) => (
                  <span key={item.text} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 text-gray-600 text-[11px] font-semibold rounded-full whitespace-nowrap">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${item.icon} text-xs`}></i></span>
                    {item.text}
                  </span>
                ))}
              </div>
            </div>

            {/* カウントダウン */}
            <div
              className="rounded-xl px-5 py-5 mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(10,10,10,0.95) 100%)',
                border: '1px solid rgba(251,191,36,0.20)',
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-400/40" />
                <p
                  className="text-[11px] font-black uppercase tracking-[0.3em]"
                  style={{
                    background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  キャンペーン終了まで
                </p>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-400/40" />
              </div>
              <div className="flex items-end justify-center gap-2.5">
                {[
                  { value: String(countdown.days).padStart(2, '0'), label: '日' },
                  { value: String(countdown.hours).padStart(2, '0'), label: '時間' },
                  { value: String(countdown.minutes).padStart(2, '0'), label: '分' },
                  { value: String(countdown.seconds).padStart(2, '0'), label: '秒' },
                ].map((item, idx) => (
                  <div key={item.label} className="flex items-end gap-1">
                    {idx > 0 && <span className="text-2xl font-black text-amber-400/50 leading-none mb-2.5">:</span>}
                    <div className="flex flex-col items-center">
                      <span className="text-[3.2rem] font-black text-white leading-none tabular-nums tracking-tight">{item.value}</span>
                      <span className="text-[10px] text-amber-400/75 font-semibold tracking-widest mt-1">{item.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTAボタン */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="text-[11px] text-white/35 flex items-center gap-1">
                <i className="ri-lock-line text-xs"></i>安全な決済
              </span>
              <span className="text-white/20 text-xs">|</span>
              <span className="text-[11px] text-white/35 flex items-center gap-1">
                <i className="ri-arrow-go-back-line text-xs"></i>30日返品保証
              </span>
              <span className="text-white/20 text-xs">|</span>
              <span className="text-[11px] text-white/35 flex items-center gap-1">
                <i className="ri-close-circle-line text-xs"></i>キャンセル可
              </span>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
